import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  UserPlus, Clock, CheckCircle, XCircle, Eye, Search,
  Calendar, User, Mail, Phone, FileText, AlertCircle, X, RefreshCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import { useRequests } from '../context/RequestsContext';
import { useAuth } from '../context/AuthContext';
import { ApiService, ClientRequestDto, RequestStatus } from '../services/api';
import { authService } from '../services/authService';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';

type UiStatusFilter = 'ALL' | RequestStatus;

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 2.5rem;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div<{ theme: any }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #973031 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
`;

const TitleText = styled.div``;

const Title = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 2.25rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: ${slideInLeft} 0.6s ease-out;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div<{ theme: any; color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}15;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div<{ theme: any }>`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div<{ theme: any }>`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 500;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const NoticeCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  border: 1px solid #f59e0b33;
  background: linear-gradient(135deg, #f59e0b12 0%, ${props => props.theme.colors.surface} 100%);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }
`;

const NoticeCardButton = styled.div`
  width: 100%;
  outline: none;
`;

const NoticeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
`;

const NoticeIcon = styled.div`
  color: #f59e0b;
  flex: 0 0 auto;
  margin-top: 0.1rem;
`;

const NoticeTitle = styled.div<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  font-weight: 800;
  margin-bottom: 0.2rem;
`;

const NoticeText = styled.div<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.45;
`;

const ScheduledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const ScheduledItem = styled.div<{ theme: any }>`
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  border-radius: 12px;
  padding: 1rem;
`;

const ScheduledItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ScheduledItemTitle = styled.div<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  font-weight: 700;
`;

const ScheduledItemMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SearchIcon = styled(Search).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textSecondary};
  pointer-events: none;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const FilterButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', 'active'].includes(prop)
})<{ theme: any; active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.primary}10;
    color: ${props => props.active ? 'white' : props.theme.colors.primary};
  }
`;

const RequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RequestCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const RequestHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const RequestInfo = styled.div`
  flex: 1;
`;

const RequestTitle = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RequestMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const MetaItem = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const StatusBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'status'].includes(prop)
})<{ theme: any; status: RequestStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'PENDING':
        return `
          background: #f59e0b20;
          color: #f59e0b;
        `;
      case 'APPROVED':
        return `
          background: #16a34a20;
          color: #16a34a;
        `;
      case 'REJECTED':
        return `
          background: #dc262620;
          color: #dc2626;
        `;
      case 'CANCELLED':
        return `
          background: #6b728020;
          color: #6b7280;
        `;
      case 'DISENGAGED':
        return `
          background: #7c3aed20;
          color: #7c3aed;
        `;
      case 'EXPIRED':
        return `
          background: #92400e20;
          color: #92400e;
        `;
      default:
        return `
          background: ${props.theme.colors.border};
          color: ${props.theme.colors.textSecondary};
        `;
    }
  }}
`;

const RequestMessage = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const RequestActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

// Modal Components
const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  padding: 0;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: all 0.3s ease;
`;

const ModalHeader = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
  max-height: 70vh;
  overflow-y: auto;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailLabel = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  font-weight: 500;
  word-break: break-word;
`;

const MessageBox = styled.div<{ theme: any }>`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const MessageText = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
`;


const ModalActions = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem 2rem 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const RequestsPage: React.FC = () => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { setPendingRequestsCount, refreshPendingRequestsCount } = useRequests();
  const { user, currentUser } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Requests');

  const isOwnerRole = useMemo(() => {
    const roleFromMe = currentUser?.role;
    if (typeof roleFromMe === 'string' && roleFromMe.trim()) {
      return roleFromMe.trim().toUpperCase() === 'OWNER';
    }
    // Fallback to token claims if /me hasn't loaded yet
    const p: any = user?.profile || {};
    const direct = [
      p?.role,
      p?.teamRole,
      p?.team_role,
      p?.userRole,
      p?.user_role,
      p?.portalRole,
      p?.portal_role,
    ].find((v) => typeof v === 'string' && v.trim());
    if (direct) return String(direct).toUpperCase() === 'OWNER';
    const roles = Array.isArray(p?.roles) ? p.roles : null;
    if (roles && roles.some((r: any) => String(r).toUpperCase() === 'OWNER')) return true;
    return false;
  }, [currentUser?.role, user?.profile]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<UiStatusFilter>('ALL');
  const [selectedRequest, setSelectedRequest] = useState<ClientRequestDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [scheduledModalOpen, setScheduledModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    type: 'approve' | 'reject' | null;
    requestId: string | null;
    requestTitle: string;
  }>({
    isOpen: false,
    type: null,
    requestId: null,
    requestTitle: ''
  });

  const [requests, setRequests] = useState<ClientRequestDto[]>([]);

  const loadRequests = async (opts?: { silent?: boolean }) => {
    const token = authService.getAccessToken();
    if (!token) return;
    if (!opts?.silent) setIsLoading(true);
    setError(null);
    try {
      const resp = await ApiService.listRequests(token, { role: 'consultant' });
      setRequests(Array.isArray(resp.data) ? resp.data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load requests');
    } finally {
      if (!opts?.silent) setIsLoading(false);
    }
  };

  const getRequesterData = useCallback((r: ClientRequestDto) => {
    const info: any = r.requesterInfo || {};
    // Current backend shape: requesterInfo.data.{name,email,phone,...}
    if (info?.data && typeof info.data === 'object') return info.data;
    // Older fallbacks
    if (info?.taxIdLookup && typeof info.taxIdLookup === 'object') return info.taxIdLookup;
    return info;
  }, []);

  const getRequesterName = useCallback((r: ClientRequestDto) => {
    const d: any = getRequesterData(r);
    return (typeof d?.name === 'string' && d.name.trim()) ? d.name.trim() : null;
  }, [getRequesterData]);

  const getRequesterEmail = useCallback((r: ClientRequestDto) => {
    const d: any = getRequesterData(r);
    const fromLookup = (typeof d?.email === 'string' && d.email.trim()) ? d.email.trim() : null;
    if (fromLookup) return fromLookup;
    return (typeof r.requestorEmail === 'string' && r.requestorEmail.trim()) ? r.requestorEmail.trim() : null;
  }, [getRequesterData]);

  const getRequesterPhone = useCallback((r: ClientRequestDto) => {
    const d: any = getRequesterData(r);
    return (typeof d?.phone === 'string' && d.phone.trim()) ? d.phone.trim() : null;
  }, [getRequesterData]);

  const filteredRequests = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return requests.filter((r) => {
      const name = (getRequesterName(r) || '').toLowerCase();
      const email = (getRequesterEmail(r) || '').toLowerCase();
      const phone = (getRequesterPhone(r) || '').toLowerCase();
      const taxId = (r.taxId || '').toLowerCase();
      const msg = (r.message || '').toLowerCase();
      const matchesSearch =
        !q ||
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        taxId.includes(q) ||
        msg.includes(q) ||
        (r.consultantTaxId || '').toLowerCase().includes(q) ||
        (r.consultantCode || '').toLowerCase().includes(q);

      const matchesStatus = statusFilter === 'ALL' || r.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [requests, searchTerm, statusFilter, getRequesterEmail, getRequesterName, getRequesterPhone]);

  const stats = useMemo(() => {
    const pending = requests.filter(r => r.status === 'PENDING').length;
    const approved = requests.filter(r => r.status === 'APPROVED').length;
    const rejected = requests.filter(r => r.status === 'REJECTED').length;
    const cancelled = requests.filter(r => r.status === 'CANCELLED').length;
    const disengaged = requests.filter(r => r.status === 'DISENGAGED').length;
    const expired = requests.filter(r => r.status === 'EXPIRED').length;
    const total = requests.length;

    return { pending, approved, rejected, cancelled, disengaged, expired, total };
  }, [requests]);

  const scheduledDisengagements = useMemo(() => {
    const now = Date.now();
    return requests
      .filter((r) => {
        if (!r.disengageAt) return false;
        const disengageAt = new Date(r.disengageAt).getTime();
        return !Number.isNaN(disengageAt) && disengageAt > now && r.status !== 'DISENGAGED';
      })
      .sort((a, b) => {
        const left = new Date(a.disengageAt || 0).getTime();
        const right = new Date(b.disengageAt || 0).getTime();
        return left - right;
      });
  }, [requests]);

  const nextScheduledDisengagement = scheduledDisengagements[0] || null;

  useEffect(() => {
    if (!isOwnerRole) return;
    setPendingRequestsCount(stats.pending);
  }, [isOwnerRole, setPendingRequestsCount, stats.pending]);

  useEffect(() => {
    if (!isOwnerRole) return;
    void loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnerRole]);

  const handleApprove = (requestId: string, requestTitle: string) => {
    setConfirmationModal({
      isOpen: true,
      type: 'approve',
      requestId,
      requestTitle
    });
  };

  const handleReject = (requestId: string, requestTitle: string) => {
    setConfirmationModal({
      isOpen: true,
      type: 'reject',
      requestId,
      requestTitle
    });
  };

  const confirmAction = async () => {
    if (!confirmationModal.requestId || !confirmationModal.type) return;
    const token = authService.getAccessToken();
    if (!token) return;

    const id = confirmationModal.requestId;
    setActionLoadingId(id);
    try {
      const resp =
        confirmationModal.type === 'approve'
          ? await ApiService.approveRequest(token, id)
          : await ApiService.rejectRequest(token, id);

      const updated = resp.data;
      setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
      await refreshPendingRequestsCount();
      showToast(
        confirmationModal.type === 'approve'
          ? toast.success('Request Approved', 'Client request has been approved successfully')
          : toast.success('Request Rejected', 'Client request has been rejected'),
      );
    } catch (e: any) {
      showToast(toast.error('Action Failed', e?.message || 'Unable to process request'));
    } finally {
      setActionLoadingId(null);
      setConfirmationModal({
        isOpen: false,
        type: null,
        requestId: null,
        requestTitle: ''
      });
    }
  };

  const cancelAction = () => {
    setConfirmationModal({
      isOpen: false,
      type: null,
      requestId: null,
      requestTitle: ''
    });
  };

  const handleViewDetails = (request: ClientRequestDto) => {
    setSelectedRequest(request);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRequest(null);
  };

  const handleOpenScheduledModal = () => {
    if (scheduledDisengagements.length === 0) return;
    setScheduledModalOpen(true);
  };

  const handleCloseScheduledModal = () => {
    setScheduledModalOpen(false);
  };

  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case 'PENDING': return <Clock size={12} />;
      case 'APPROVED': return <CheckCircle size={12} />;
      case 'REJECTED': return <XCircle size={12} />;
      case 'CANCELLED': return <X size={12} />;
      case 'DISENGAGED': return <AlertCircle size={12} />;
      case 'EXPIRED': return <AlertCircle size={12} />;
      default: return <AlertCircle size={12} />;
    }
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  };

  if (!isOwnerRole) {
    return (
      <Container>
        <Card>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontWeight: 900, fontSize: '1.25rem', color: theme.colors.text, marginBottom: '0.5rem' }}>
              Access Denied
            </div>
            <div style={{ color: theme.colors.textSecondary, fontWeight: 600, marginBottom: '1.25rem' }}>
              You do not have permission to access Requests.
            </div>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderSection>
        <TopRow>
          <TitleGroup>
            <IconWrapper theme={theme}>
              <UserPlus size={24} />
            </IconWrapper>
            <TitleText>
              <Title theme={theme}>Client Requests</Title>
              <Subtitle theme={theme}>Manage and respond to client consultation requests</Subtitle>
            </TitleText>
          </TitleGroup>
          <Actions>
            <Button
              variant="outline"
              onClick={() => loadRequests()}
              disabled={isLoading}
            >
              <RefreshCcw size={16} style={{ marginRight: '0.5rem' }} />
              Refresh
            </Button>
          </Actions>
        </TopRow>

        <StatsRow>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color={theme.colors.primary}>
              <UserPlus size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.total}</StatValue>
              <StatLabel theme={theme}>Total Requests</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color="#f59e0b">
              <Clock size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.pending}</StatValue>
              <StatLabel theme={theme}>Pending Requests</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color="#16a34a">
              <CheckCircle size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.approved}</StatValue>
              <StatLabel theme={theme}>Approved</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color="#dc2626">
              <XCircle size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.rejected}</StatValue>
              <StatLabel theme={theme}>Rejected</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color="#6b7280">
              <X size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.cancelled}</StatValue>
              <StatLabel theme={theme}>Cancelled</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color="#7c3aed">
              <AlertCircle size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.disengaged}</StatValue>
              <StatLabel theme={theme}>Disengaged</StatLabel>
            </StatContent>
          </StatCard>
          <StatCard theme={theme}>
            <StatIcon theme={theme} color="#92400e">
              <AlertCircle size={20} />
            </StatIcon>
            <StatContent>
              <StatValue theme={theme}>{stats.expired}</StatValue>
              <StatLabel theme={theme}>Expired</StatLabel>
            </StatContent>
          </StatCard>
        </StatsRow>
      </HeaderSection>

      {scheduledDisengagements.length > 0 && (
        <NoticeCardButton
          role="button"
          tabIndex={0}
          onClick={handleOpenScheduledModal}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpenScheduledModal();
            }
          }}
        >
          <NoticeCard theme={theme}>
            <NoticeRow>
              <NoticeIcon>
                <AlertCircle size={20} />
              </NoticeIcon>
              <div>
                <NoticeTitle theme={theme}>Scheduled Disengagement</NoticeTitle>
                <NoticeText theme={theme}>
                  {scheduledDisengagements.length} request{scheduledDisengagements.length > 1 ? 's are' : ' is'} scheduled to be disengaged.
                  {nextScheduledDisengagement?.disengageAt
                    ? ` Next disengagement: ${formatDate(nextScheduledDisengagement.disengageAt)}.`
                    : ''}
                  {' '}Click to view affected requests.
                </NoticeText>
              </div>
            </NoticeRow>
          </NoticeCard>
        </NoticeCardButton>
      )}

      <FilterSection>
        <SearchContainer>
          <SearchIcon theme={theme} size={16} />
          <SearchInput
            theme={theme}
            type="text"
            placeholder="Search requests by client name, company, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        <FilterButtons>
          <FilterButton
            theme={theme}
            active={statusFilter === 'ALL'}
            onClick={() => setStatusFilter('ALL')}
          >
            All
          </FilterButton>
          <FilterButton
            theme={theme}
            active={statusFilter === 'PENDING'}
            onClick={() => setStatusFilter('PENDING')}
          >
            Pending
          </FilterButton>
          <FilterButton
            theme={theme}
            active={statusFilter === 'APPROVED'}
            onClick={() => setStatusFilter('APPROVED')}
          >
            Approved
          </FilterButton>
          <FilterButton
            theme={theme}
            active={statusFilter === 'REJECTED'}
            onClick={() => setStatusFilter('REJECTED')}
          >
            Rejected
          </FilterButton>
          <FilterButton
            theme={theme}
            active={statusFilter === 'CANCELLED'}
            onClick={() => setStatusFilter('CANCELLED')}
          >
            Cancelled
          </FilterButton>
          <FilterButton
            theme={theme}
            active={statusFilter === 'DISENGAGED'}
            onClick={() => setStatusFilter('DISENGAGED')}
          >
            Disengaged
          </FilterButton>
          <FilterButton
            theme={theme}
            active={statusFilter === 'EXPIRED'}
            onClick={() => setStatusFilter('EXPIRED')}
          >
            Expired
          </FilterButton>
        </FilterButtons>
      </FilterSection>

      {error && (
        <Card>
          <div style={{ padding: '1rem 1.25rem', color: theme.colors.text }}>
            <div style={{ fontWeight: 900, marginBottom: '0.25rem' }}>Could not load requests</div>
            <div style={{ color: theme.colors.textSecondary, fontWeight: 600 }}>{error}</div>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card>
          <div style={{ padding: '1rem 1.25rem', color: theme.colors.textSecondary, fontWeight: 700 }}>
            Loading requests...
          </div>
        </Card>
      )}

      <RequestsList>
        {filteredRequests.map((request) => (
          <RequestCard key={request.id} theme={theme}>
            <RequestHeader>
              <RequestInfo>
                <RequestTitle theme={theme}>
                  <FileText size={18} />
                  Request from {getRequesterName(request) || request.taxId}
                </RequestTitle>
                <RequestMeta>
                  <MetaItem theme={theme}>
                    <User size={14} />
                    {getRequesterName(request) || '—'}
                  </MetaItem>
                  <MetaItem theme={theme}>
                    <Mail size={14} />
                    {getRequesterEmail(request) || '—'}
                  </MetaItem>
                  <MetaItem theme={theme}>
                    <Phone size={14} />
                    {getRequesterPhone(request) || '—'}
                  </MetaItem>
                  <MetaItem theme={theme}>Tax ID: {request.taxId}</MetaItem>
                  <MetaItem theme={theme}>
                    <Calendar size={14} />
                    Requested: {formatDate(request.createdAt)}
                  </MetaItem>
                  <MetaItem theme={theme}>
                    <Calendar size={14} />
                    Deadline: {formatDate(request.expectedDeadline ?? null)}
                  </MetaItem>
                </RequestMeta>
              </RequestInfo>
              <StatusBadge theme={theme} status={request.status}>
                {getStatusIcon(request.status)}
                {request.status}
              </StatusBadge>
            </RequestHeader>
            
            <RequestMessage theme={theme}>
              {request.message || '—'}
            </RequestMessage>
            
            <RequestActions>
              <Button 
                variant="outline" 
                size="small"
                onClick={() => handleViewDetails(request)}
              >
                <Eye size={14} style={{ marginRight: '0.5rem' }} />
                View Details
              </Button>
              {request.status === 'PENDING' && (
                <>
                  <Button 
                    size="small"
                    disabled={actionLoadingId === request.id}
                    onClick={() => handleApprove(request.id, `Request from ${getRequesterName(request) || request.taxId}`)}
                  >
                    <CheckCircle size={14} style={{ marginRight: '0.5rem' }} />
                    Approve
                  </Button>
                  <Button 
                    variant="outline" 
                    size="small"
                    disabled={actionLoadingId === request.id}
                    onClick={() => handleReject(request.id, `Request from ${getRequesterName(request) || request.taxId}`)}
                  >
                    <XCircle size={14} style={{ marginRight: '0.5rem' }} />
                    Reject
                  </Button>
                </>
              )}
            </RequestActions>
          </RequestCard>
        ))}
      </RequestsList>

      {filteredRequests.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <UserPlus size={48} style={{ color: theme.colors.textSecondary, marginBottom: '1rem' }} />
            <h3 style={{ color: theme.colors.text, marginBottom: '0.5rem' }}>No Requests Found</h3>
            <p style={{ color: theme.colors.textSecondary, margin: 0 }}>
              {searchTerm || statusFilter !== 'ALL' 
                ? 'No requests match your current filters.' 
                : 'No client requests available at the moment.'}
            </p>
          </div>
        </Card>
      )}

      <ModalOverlay theme={theme} isOpen={scheduledModalOpen} onClick={handleCloseScheduledModal}>
        <ModalContent
          theme={theme}
          isOpen={scheduledModalOpen}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader theme={theme}>
            <ModalTitle theme={theme}>
              <AlertCircle size={24} />
              Requests Scheduled for Disengagement
            </ModalTitle>
            <CloseButton theme={theme} onClick={handleCloseScheduledModal}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <ScheduledList>
              {scheduledDisengagements.map((request) => (
                <ScheduledItem key={request.id} theme={theme}>
                  <ScheduledItemTop>
                    <ScheduledItemTitle theme={theme}>
                      {getRequesterName(request) || request.taxId}
                    </ScheduledItemTitle>
                    <StatusBadge theme={theme} status={request.status}>
                      {getStatusIcon(request.status)}
                      {request.status}
                    </StatusBadge>
                  </ScheduledItemTop>

                  <ScheduledItemMeta>
                    <DetailItem>
                      <DetailLabel theme={theme}>Tax ID</DetailLabel>
                      <DetailValue theme={theme}>{request.taxId || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Email</DetailLabel>
                      <DetailValue theme={theme}>{getRequesterEmail(request) || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Phone</DetailLabel>
                      <DetailValue theme={theme}>{getRequesterPhone(request) || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Disengage At</DetailLabel>
                      <DetailValue theme={theme}>{formatDate(request.disengageAt ?? null)}</DetailValue>
                    </DetailItem>
                  </ScheduledItemMeta>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => {
                        setSelectedRequest(request);
                        setScheduledModalOpen(false);
                        setModalOpen(true);
                      }}
                    >
                      <Eye size={14} style={{ marginRight: '0.5rem' }} />
                      View Request
                    </Button>
                  </div>
                </ScheduledItem>
              ))}
            </ScheduledList>
          </ModalBody>

          <ModalActions theme={theme}>
            <Button variant="outline" onClick={handleCloseScheduledModal}>
              Close
            </Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* Request Details Modal */}
      <ModalOverlay theme={theme} isOpen={modalOpen} onClick={handleCloseModal}>
        <ModalContent 
          theme={theme} 
          isOpen={modalOpen} 
          onClick={(e) => e.stopPropagation()}
        >
          {selectedRequest && (
            <>
              <ModalHeader theme={theme}>
                <ModalTitle theme={theme}>
                  <FileText size={24} />
                  Request Details - {selectedRequest.id}
                </ModalTitle>
                <CloseButton theme={theme} onClick={handleCloseModal}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                {/* Requester Information */}
                <DetailSection>
                  <SectionTitle theme={theme}>
                    <User size={18} />
                    Requester Information
                  </SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel theme={theme}>Name</DetailLabel>
                      <DetailValue theme={theme}>{getRequesterName(selectedRequest) || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Email</DetailLabel>
                      <DetailValue theme={theme}>{getRequesterEmail(selectedRequest) || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Phone</DetailLabel>
                      <DetailValue theme={theme}>{getRequesterPhone(selectedRequest) || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Tax ID</DetailLabel>
                      <DetailValue theme={theme}>{selectedRequest.taxId}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </DetailSection>

                {/* Request Information */}
                <DetailSection>
                  <SectionTitle theme={theme}>
                    <FileText size={18} />
                    Request Information
                  </SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel theme={theme}>Consultant Tax ID</DetailLabel>
                      <DetailValue theme={theme}>{selectedRequest.taxId || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Status</DetailLabel>
                      <DetailValue theme={theme}>
                        <StatusBadge theme={theme} status={selectedRequest.status}>
                          {getStatusIcon(selectedRequest.status)}
                          {selectedRequest.status}
                        </StatusBadge>
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Requested At</DetailLabel>
                      <DetailValue theme={theme}>{formatDate(selectedRequest.createdAt)}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel theme={theme}>Expected Deadline</DetailLabel>
                      <DetailValue theme={theme}>{formatDate(selectedRequest.expectedDeadline ?? null)}</DetailValue>
                    </DetailItem>
                  </DetailGrid>

                  <SectionTitle theme={theme} style={{ marginTop: '1.5rem' }}>
                    Request Message
                  </SectionTitle>
                  <MessageBox theme={theme}>
                    <MessageText theme={theme}>{selectedRequest.message || '—'}</MessageText>
                  </MessageBox>
                </DetailSection>
              </ModalBody>

              <ModalActions theme={theme}>
                <Button variant="outline" onClick={handleCloseModal}>
                  Close
                </Button>
                {selectedRequest.status === 'PENDING' && (
                  <>
                    <Button 
                      onClick={() => {
                        handleApprove(selectedRequest.id, `Request from ${getRequesterName(selectedRequest) || selectedRequest.taxId}`);
                        handleCloseModal();
                      }}
                    >
                      <CheckCircle size={16} style={{ marginRight: '0.5rem' }} />
                      Approve Request
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        handleReject(selectedRequest.id, `Request from ${getRequesterName(selectedRequest) || selectedRequest.taxId}`);
                        handleCloseModal();
                      }}
                    >
                      <XCircle size={16} style={{ marginRight: '0.5rem' }} />
                      Reject Request
                    </Button>
                  </>
                )}
              </ModalActions>
            </>
          )}
        </ModalContent>
      </ModalOverlay>

      {/* Confirmation Modal */}
      <ModalOverlay theme={theme} isOpen={confirmationModal.isOpen} onClick={cancelAction}>
        <ModalContent 
          theme={theme} 
          isOpen={confirmationModal.isOpen} 
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader theme={theme}>
            <ModalTitle theme={theme}>
              {confirmationModal.type === 'approve' ? (
                <>
                  <CheckCircle size={24} />
                  Confirm Approval
                </>
              ) : (
                <>
                  <XCircle size={24} />
                  Confirm Rejection
                </>
              )}
            </ModalTitle>
            <CloseButton theme={theme} onClick={cancelAction}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <p style={{ 
                color: theme.colors.text, 
                fontSize: '1.1rem', 
                marginBottom: '1rem',
                fontWeight: '500'
              }}>
                Are you sure you want to {confirmationModal.type} this request?
              </p>
              <div style={{
                background: theme.colors.background,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <strong style={{ color: theme.colors.text }}>
                  {confirmationModal.requestTitle}
                </strong>
              </div>
              <p style={{ 
                color: theme.colors.textSecondary, 
                fontSize: '0.95rem',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {confirmationModal.type === 'approve' 
                  ? 'This action will approve the client request and notify them of the approval.'
                  : 'This action will reject the client request and notify them of the rejection.'
                }
              </p>
            </div>
          </ModalBody>

          <ModalActions theme={theme}>
            <Button variant="outline" onClick={cancelAction}>
              Cancel
            </Button>
            <button
              onClick={confirmAction}
              style={{
                background: confirmationModal.type === 'approve' ? '#16a34a' : '#dc2626',
                borderColor: confirmationModal.type === 'approve' ? '#16a34a' : '#dc2626',
                color: 'white',
                border: `1px solid ${confirmationModal.type === 'approve' ? '#16a34a' : '#dc2626'}`,
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                const hoverColor = confirmationModal.type === 'approve' ? '#15803d' : '#b91c1c';
                e.currentTarget.style.background = hoverColor;
                e.currentTarget.style.borderColor = hoverColor;
              }}
              onMouseOut={(e) => {
                const normalColor = confirmationModal.type === 'approve' ? '#16a34a' : '#dc2626';
                e.currentTarget.style.background = normalColor;
                e.currentTarget.style.borderColor = normalColor;
              }}
            >
              {confirmationModal.type === 'approve' ? (
                <>
                  <CheckCircle size={16} />
                  Approve Request
                </>
              ) : (
                <>
                  <XCircle size={16} />
                  Reject Request
                </>
              )}
            </button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>
    </Container>
  );
};

export default RequestsPage;
